import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getPlatformProductAnalytics } from '../services/adminAnalyticsService';
import TimeRangeSelector from '../components/TimeRangeSelector';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { ORDERS_TABLE_ID, PRODUCTS_TABLE_ID } from '../lib/appwrite';
import './AnalyticsPage.css';

const formatCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-TZ', { style: 'decimal' });
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [timeRange, setTimeRange] = useState('month');

  const loadAnalytics = useCallback(() => {
    setLoading(true);
    getPlatformProductAnalytics({ timeRange })
      .then(setData)
      .catch((err) => setError(err.message || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [timeRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useAutoRefresh(loadAnalytics, { realtimeTables: [ORDERS_TABLE_ID, PRODUCTS_TABLE_ID] });

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="dashboard-spinner" />
        <p>Loading platform analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <div className="analytics-error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, byCategory, byStore, topByRevenue, topByUnits, topByOrders, lowStock, outOfStock, byMonth, insights = [], performance = {} } = data;

  const chartColors = ['#039855', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5', '#047857', '#065f46'];
  const revenueChartData = byMonth.map((m) => ({ name: m.label, revenue: m.revenue, orders: m.orders }));
  const categoryChartData = byCategory.slice(0, 8).map((c) => ({ name: c.name, value: c.revenue }));
  const topProductsChartData = topByRevenue.map((p, i) => ({ name: p.productName?.slice(0, 18) || `#${i + 1}`, revenue: p.revenue }));

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'insights', label: 'Where to Improve' },
    { id: 'categories', label: 'By Category' },
    { id: 'top-products', label: 'Top Products' },
    { id: 'inventory', label: 'Stock Alerts' },
    { id: 'stores', label: 'By Store' },
    { id: 'trends', label: 'Monthly Trends' },
  ];

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div>
          <h1 className="dashboard-title">Platform Product Analytics</h1>
          <p className="dashboard-subtitle">
            Platform-wide analytics — aggregated across all stores. No store-level breakdown.
          </p>
        </div>
        <div className="analytics-header-actions">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          <button
            type="button"
            className="analytics-refresh"
            onClick={loadAnalytics}
          >
            ⟳ Refresh
          </button>
        </div>
      </div>

      <nav className="analytics-nav">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`analytics-nav-btn ${activeSection === s.id ? 'active' : ''}`}
            onClick={() => setActiveSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </nav>

      <main className="analytics-content">
        {activeSection === 'overview' && (
          <>
            <p className="analytics-desc" style={{ marginBottom: 16 }}>Platform-wide totals — aggregated across all stores</p>
            <div className="analytics-cards">
              <div className="analytics-card">
                <span className="analytics-card-value">{summary.totalProducts}</span>
                <span className="analytics-card-label">Total Products</span>
              </div>
              <div className="analytics-card">
                <span className="analytics-card-value">{summary.totalCategories}</span>
                <span className="analytics-card-label">Categories</span>
              </div>
              <div className="analytics-card">
                <span className="analytics-card-value">{summary.totalStoresWithProducts}</span>
                <span className="analytics-card-label">Stores with Products</span>
              </div>
              <div className="analytics-card analytics-card-revenue">
                <span className="analytics-card-value">{formatCurrency(summary.totalProductRevenue)}</span>
                <span className="analytics-card-label">Total Product Revenue (TZS)</span>
              </div>
              <div className="analytics-card">
                <span className="analytics-card-value">{formatCurrency(summary.totalUnitsSold)}</span>
                <span className="analytics-card-label">Units Sold</span>
              </div>
              <div className="analytics-card">
                <span className="analytics-card-value">{summary.totalOrders}</span>
                <span className="analytics-card-label">Total Orders</span>
              </div>
              <div className="analytics-card">
                <span className="analytics-card-value">{formatCurrency(performance.avgOrderValue || 0)}</span>
                <span className="analytics-card-label">Avg Order Value (TZS)</span>
              </div>
              <div className="analytics-card analytics-card-warn">
                <span className="analytics-card-value">{summary.lowStockCount}</span>
                <span className="analytics-card-label">Low Stock (&lt;50)</span>
              </div>
              <div className="analytics-card analytics-card-danger">
                <span className="analytics-card-value">{summary.outOfStockCount}</span>
                <span className="analytics-card-label">Out of Stock</span>
              </div>
            </div>
            {(performance.revenueGrowth !== undefined || performance.ordersGrowth !== undefined) && (
              <div className="analytics-performance-row">
                {performance.revenueGrowth !== undefined && (
                  <div className={`analytics-growth ${performance.revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                    <strong>Revenue vs last month:</strong> {performance.revenueGrowth >= 0 ? '+' : ''}{performance.revenueGrowth.toFixed(1)}%
                  </div>
                )}
                {performance.ordersGrowth !== undefined && (
                  <div className={`analytics-growth ${performance.ordersGrowth >= 0 ? 'positive' : 'negative'}`}>
                    <strong>Orders vs last month:</strong> {performance.ordersGrowth >= 0 ? '+' : ''}{performance.ordersGrowth.toFixed(1)}%
                  </div>
                )}
              </div>
            )}

            <h3 className="analytics-subsection" style={{ marginTop: 32 }}>Revenue &amp; Orders Over Time (Last 12 Months)</h3>
            <div className="analytics-chart-wrap">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <YAxis yAxisId="left" tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} stroke="#6b7280" />
                  <YAxis yAxisId="right" orientation="right" dataKey="orders" tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <Tooltip formatter={(v, name) => [name?.includes('Revenue') ? formatCurrency(v) : v, name]} labelFormatter={(l) => l} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue (TZS)" stroke="#039855" fill="#10b981" fillOpacity={0.4} strokeWidth={2} />
                  <Area yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke="#f79009" fill="#f79009" fillOpacity={0.3} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="analytics-overview-grid">
              <div className="analytics-overview-col">
                <h3 className="analytics-subsection">Revenue by Category (Top 8)</h3>
                <div className="analytics-chart-wrap analytics-chart-pie">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={categoryChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {categoryChartData.map((_, i) => (
                          <Cell key={i} fill={chartColors[i % chartColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="analytics-overview-col">
                <h3 className="analytics-subsection">Top 10 Products by Revenue</h3>
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={topProductsChartData} layout="vertical" margin={{ left: 20, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} stroke="#6b7280" />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} stroke="#6b7280" />
                      <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
                      <Bar dataKey="revenue" name="Revenue (TZS)" fill="#039855" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <h3 className="analytics-subsection">Monthly Revenue &amp; Orders (Bar)</h3>
            <div className="analytics-chart-wrap">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revenueChartData} margin={{ top: 20, right: 50, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6b7280" />
                  <YAxis yAxisId="left" tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} stroke="#6b7280" />
                  <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
                  <Tooltip formatter={(v, name) => [name === 'Revenue (TZS)' ? formatCurrency(v) : v, name]} contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                  <Bar yAxisId="left" dataKey="revenue" name="Revenue (TZS)" fill="#039855" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="orders" name="Orders" fill="#f79009" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <h3 className="analytics-subsection">Top Categories (Revenue)</h3>
            <div className="analytics-table-wrap analytics-table-compact">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Products</th>
                    <th>Units Sold</th>
                    <th>Revenue (TZS)</th>
                  </tr>
                </thead>
                <tbody>
                  {byCategory.slice(0, 8).map((c) => (
                    <tr key={c.name}>
                      <td><strong>{c.name}</strong></td>
                      <td>{c.count}</td>
                      <td>{formatCurrency(c.units)}</td>
                      <td>{formatCurrency(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {insights.length > 0 && (
              <>
                <h3 className="analytics-subsection">Quick Insights</h3>
                <div className="analytics-insights-grid analytics-insights-compact">
                  {insights.slice(0, 6).map((item, i) => (
                    <div key={i} className={`analytics-insight-card analytics-insight-${item.type}`}>
                      <div className="analytics-insight-header">
                        <span className="analytics-insight-title">{item.title}</span>
                        {(item.count ?? item.value) && (
                          <span className="analytics-insight-count">{item.count ?? item.value}</span>
                        )}
                      </div>
                      <p className="analytics-insight-action">{item.action}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {activeSection === 'insights' && (
          <div className="analytics-section">
            <h2>Where to Improve</h2>
            <p className="analytics-desc">Actionable insights to focus your efforts and grow performance</p>
            {insights.length === 0 ? (
              <p className="analytics-empty">No urgent issues — keep monitoring stock and trends.</p>
            ) : (
              <div className="analytics-insights-grid">
                {insights.map((item, i) => (
                  <div key={i} className={`analytics-insight-card analytics-insight-${item.type}`}>
                    <div className="analytics-insight-header">
                      <span className="analytics-insight-title">{item.title}</span>
                      {(item.count ?? item.value) && (
                        <span className="analytics-insight-count">{item.count ?? item.value}</span>
                      )}
                    </div>
                    <p className="analytics-insight-action">{item.action}</p>
                  </div>
                ))}
              </div>
            )}
            {performance.worstCategories?.length > 0 && performance.worstCategories.some((c) => (c.revenue || 0) > 0) && (
              <>
                <h3 className="analytics-subsection">Lowest-performing categories (by revenue)</h3>
                <p className="analytics-desc">Focus marketing or promotions on these categories</p>
                <div className="analytics-table-wrap">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Revenue (TZS)</th>
                        <th>Units Sold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performance.worstCategories.filter((c) => (c.revenue || 0) > 0).map((c) => (
                        <tr key={c.name}>
                          <td><strong>{c.name}</strong></td>
                          <td>{formatCurrency(c.revenue)}</td>
                          <td>{formatCurrency(c.units)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeSection === 'categories' && (
          <div className="analytics-section">
            <h2>Products by Category</h2>
            <p className="analytics-desc">Platform-wide — revenue and units sold per category</p>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Products</th>
                    <th>Units Sold</th>
                    <th>Revenue (TZS)</th>
                  </tr>
                </thead>
                <tbody>
                  {byCategory.map((c) => (
                    <tr key={c.name}>
                      <td><strong>{c.name}</strong></td>
                      <td>{c.count}</td>
                      <td>{formatCurrency(c.units)}</td>
                      <td>{formatCurrency(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeSection === 'top-products' && (
          <div className="analytics-section">
            <h2>Top 10 Products (Platform-Wide)</h2>
            <p className="analytics-desc">Aggregated across all stores — no store breakdown</p>

            <h3 className="analytics-subsection">By Revenue</h3>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Units Sold</th>
                    <th>Revenue (TZS)</th>
                    <th>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {topByRevenue.map((p, i) => (
                    <tr key={`rev-${i}`}>
                      <td>{i + 1}</td>
                      <td>
                        {p.productRowId ? (
                          <button
                            type="button"
                            className="analytics-link"
                            onClick={() => navigate(`/dashboard/products/${p.productRowId}`)}
                          >
                            {p.productName}
                          </button>
                        ) : (
                          p.productName
                        )}
                      </td>
                      <td>{p.category || '—'}</td>
                      <td>{formatCurrency(p.unitsSold)}</td>
                      <td><strong>{formatCurrency(p.revenue)}</strong></td>
                      <td>{p.orderCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="analytics-subsection">By Units Sold</h3>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Units Sold</th>
                    <th>Revenue (TZS)</th>
                  </tr>
                </thead>
                <tbody>
                  {topByUnits.map((p, i) => (
                    <tr key={`units-${i}`}>
                      <td>{i + 1}</td>
                      <td>
                        {p.productRowId ? (
                          <button
                            type="button"
                            className="analytics-link"
                            onClick={() => navigate(`/dashboard/products/${p.productRowId}`)}
                          >
                            {p.productName}
                          </button>
                        ) : (
                          p.productName
                        )}
                      </td>
                      <td>{p.category || '—'}</td>
                      <td><strong>{formatCurrency(p.unitsSold)}</strong></td>
                      <td>{formatCurrency(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="analytics-subsection">By Order Count</h3>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Orders</th>
                    <th>Units Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {topByOrders.map((p, i) => (
                    <tr key={`ord-${i}`}>
                      <td>{i + 1}</td>
                      <td>
                        {p.productRowId ? (
                          <button
                            type="button"
                            className="analytics-link"
                            onClick={() => navigate(`/dashboard/products/${p.productRowId}`)}
                          >
                            {p.productName}
                          </button>
                        ) : (
                          p.productName
                        )}
                      </td>
                      <td>{p.category || '—'}</td>
                      <td><strong>{p.orderCount}</strong></td>
                      <td>{formatCurrency(p.unitsSold)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeSection === 'inventory' && (
          <div className="analytics-section">
            <h2>Out of Stock ({outOfStock.length})</h2>
            <p className="analytics-desc">Products with zero stock — require immediate attention</p>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Store</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {outOfStock.length === 0 ? (
                    <tr><td colSpan={5} className="analytics-empty">No out-of-stock products</td></tr>
                  ) : (
                    outOfStock.map((p) => (
                      <tr key={p.$id}>
                        <td>
                          <button
                            type="button"
                            className="analytics-link"
                            onClick={() => navigate(`/dashboard/products/${p.$id}`)}
                          >
                            {p.productName || '—'}
                          </button>
                        </td>
                        <td>{p.storeName || p.storeId || '—'}</td>
                        <td>{p.productCategory || '—'}</td>
                        <td>{formatCurrency(p.sellPrice)}</td>
                        <td>
                          <button
                            type="button"
                            className="analytics-btn-sm"
                            onClick={() => navigate(`/dashboard/products/${p.$id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <h2 style={{ marginTop: 32 }}>Low Stock — Under 50 units ({lowStock.length})</h2>
            <p className="analytics-desc">Products that may need restocking soon</p>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Store</th>
                    <th>Stock</th>
                    <th>Category</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.length === 0 ? (
                    <tr><td colSpan={5} className="analytics-empty">No low-stock products</td></tr>
                  ) : (
                    lowStock.map((p) => (
                      <tr key={p.$id}>
                        <td>
                          <button
                            type="button"
                            className="analytics-link"
                            onClick={() => navigate(`/dashboard/products/${p.$id}`)}
                          >
                            {p.productName || '—'}
                          </button>
                        </td>
                        <td>{p.storeName || p.storeId || '—'}</td>
                        <td><span className="analytics-stock-warn">{p.stockCount}</span></td>
                        <td>{p.productCategory || '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="analytics-btn-sm"
                            onClick={() => navigate(`/dashboard/products/${p.$id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeSection === 'stores' && (
          <div className="analytics-section">
            <h2>Product Count by Store</h2>
            <p className="analytics-desc">Optional store-level drill-down</p>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Store</th>
                    <th>Product Count</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {byStore.map((s) => (
                    <tr key={s.storeId}>
                      <td>
                        <button
                          type="button"
                          className="analytics-link"
                          onClick={() => navigate(`/dashboard/stores/${s.storeId}`)}
                        >
                          {s.storeName || s.storeId}
                        </button>
                      </td>
                      <td><strong>{s.count}</strong></td>
                      <td>
                        <button
                          type="button"
                          className="analytics-btn-sm"
                          onClick={() => navigate(`/dashboard/stores/${s.storeId}`)}
                        >
                          View Store
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeSection === 'trends' && (
          <div className="analytics-section">
            <h2>Monthly Trends (Last 12 Months)</h2>
            <p className="analytics-desc">Platform-wide — orders, revenue, and units sold per month</p>

            <div className="analytics-chart-wrap">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={revenueChartData} margin={{ top: 20, right: 50, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6b7280" />
                  <YAxis yAxisId="left" tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} stroke="#6b7280" />
                  <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
                  <Tooltip formatter={(v, name) => [name === 'Revenue (TZS)' ? formatCurrency(v) : v, name]} contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                  <Bar yAxisId="left" dataKey="revenue" name="Revenue (TZS)" fill="#039855" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="orders" name="Orders" fill="#f79009" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <h3 className="analytics-subsection">Monthly breakdown</h3>
            <div className="analytics-trends">
              {byMonth.map((m) => {
                const maxRev = Math.max(1, ...byMonth.map((x) => x.revenue));
                const pct = (m.revenue / maxRev) * 100;
                return (
                  <div key={m.key} className="analytics-trend-row">
                    <span className="analytics-trend-label">{m.label}</span>
                    <div className="analytics-trend-bar-wrap">
                      <div
                        className="analytics-trend-bar"
                        style={{ width: `${pct}%` }}
                        title={`${formatCurrency(m.revenue)} TZS`}
                      />
                    </div>
                    <span className="analytics-trend-meta">
                      {m.orders} orders · {formatCurrency(m.units)} units · {formatCurrency(m.revenue)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AnalyticsPage;
